import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Divider, Toggle, Select, Tooltip } from '@ets/design-system';

import { useSelector } from '_common/hooks';

import styles from './PreviewControls.module.scss';

type PreviewControlsProps = {
  object: doDOC.File;
  zoom: {
    label: string;
    value: string;
  };
  setZoom: React.Dispatch<
    React.SetStateAction<{
      label: string;
      value: string;
    }>
  >;
} & (
  | {
      showPagination?: true;
      previousPageIsDisabled: boolean;
      nextPageIsDisabled: boolean;
      onPreviousPage: () => void;
      onNextPage: () => void;
      currentPage: number;
      totalPageNumber: number;
    }
  | {
      showPagination?: undefined | false;
      previousPageIsDisabled?: boolean;
      nextPageIsDisabled?: boolean;
      onPreviousPage?: () => void;
      onNextPage?: () => void;
      currentPage?: number;
      totalPageNumber?: number;
    }
);

const PreviewControls = ({
  object,
  zoom,
  setZoom,
  nextPageIsDisabled,
  onNextPage,
  onPreviousPage,
  previousPageIsDisabled,
  currentPage,
  totalPageNumber,
  showPagination = true,
}: PreviewControlsProps) => {
  const intl = useIntl();
  const userId = useSelector((state) => state.auth.userId);

  useEffect(() => {
    const zoom = window.localStorage.getItem(`${userId}-${object.id}-preview-zoom`);
    if (zoom) {
      const zoomLabel = (+zoom * 100).toFixed(0);
      setZoom({ value: zoom, label: `${zoomLabel}%` });
    }
  }, [userId, object]);

  const zoomOptions = useMemo(() => {
    return [
      { value: '0.5', label: '50%' },
      { value: '1', label: '100%' },
      { value: '1.5', label: '150%' },
      { value: '2', label: '200%' },
    ];
  }, []);

  const handleZoomChange = (newValue: (typeof zoomOptions)[number] | null) => {
    if (newValue) {
      setZoom(newValue);
      window.localStorage.setItem(`${userId}-${object.id}-preview-zoom`, `${newValue.value}`);
    }
  };

  return (
    <div className={styles.controls} data-testid="object-preview-controls">
      {showPagination && (
        <>
          <Tooltip
            visible={!previousPageIsDisabled}
            content={intl.formatMessage({ id: 'PREVIOUS_PAGE' })}
            placement="top"
            testId="object-preview-controls-previous-page"
          >
            <Toggle
              size="m"
              variant="link"
              iconProps={{ icon: 'chevron-up' }}
              disabled={previousPageIsDisabled}
              onClick={onPreviousPage}
              testId="object-preview-controls-previous-page"
            />
          </Tooltip>
          <Tooltip
            visible={!nextPageIsDisabled}
            content={intl.formatMessage({ id: 'NEXT_PAGE' })}
            placement="top"
            testId="object-preview-controls-next-page"
          >
            <Toggle
              size="m"
              variant="link"
              iconProps={{
                icon: 'chevron-down',
              }}
              disabled={nextPageIsDisabled}
              onClick={onNextPage}
              style={{ margin: '0 1rem 0 0' }}
              testId="object-preview-controls-next-page"
            />
          </Tooltip>
          <span className={styles.pagination} data-testid="object-preview-controls-pagination">
            {currentPage}/{totalPageNumber}
          </span>
          <Divider vertical margin="0 2rem" />
        </>
      )}
      <Select
        variant="ghost"
        width="10rem"
        menuWidth="18rem"
        size="m"
        menuPlacement="top"
        isClearable={false}
        value={zoomOptions.find((option) => option.value === zoom.value)}
        onChange={handleZoomChange}
        options={zoomOptions}
        testId="object-preview-controls-zoom"
      />
    </div>
  );
};

export default PreviewControls;
